import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  CreditCard,
  MessageSquare,
  ShoppingBag,
  Search,
  User,
  Users,
} from 'lucide-react';
import { Box } from '@mui/material';
import { AppModal } from '../AppModal';
import { AppTextField } from '../AppTextField';
import { AppSkeleton } from '../AppSkeleton';
import { adminSearchApi } from '@/api';
import { colors, spacing, radius, fonts } from '@/theme/tokens';
import type { GlobalSearchResultItem, GlobalSearchResultType } from '@tilly/shared';

/** Debounce a fast-changing value so search doesn't fire a request on every keystroke. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
}

const SECTION_ORDER: GlobalSearchResultType[] = [
  'client',
  'appointment',
  'message',
  'payment',
  'service',
  'provider',
];

const SECTION_ICONS: Record<GlobalSearchResultType, React.ReactNode> = {
  client: <User size={14} />,
  appointment: <Calendar size={14} />,
  message: <MessageSquare size={14} />,
  payment: <CreditCard size={14} />,
  service: <ShoppingBag size={14} />,
  provider: <Users size={14} />,
};

function resultHref(result: GlobalSearchResultItem): string {
  switch (result.type) {
    case 'client':
      return `/admin/clients/${result.id}`;
    case 'appointment':
    case 'message':
    case 'payment':
      return `/admin/clients/${result.clientId}?focus=timeline`;
    case 'service':
      return '/admin/catalog';
    case 'provider':
      return '/admin/providers';
  }
}

export function CommandPalette({ open, onClose, businessId }: CommandPaletteProps) {
  const { t } = useTranslation(['admin']);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebouncedValue(query.trim(), 300);

  // Reset local state when the palette transitions from closed to open.
  // Adjusted during render (React's recommended pattern for prop-driven
  // resets) rather than in an effect, to avoid an extra render pass.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setQuery('');
      setSelectedIndex(0);
    }
  }

  useEffect(() => {
    if (!open) return;
    // Focus after the modal has finished mounting/animating in.
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open]);

  const searchQuery = useQuery({
    queryKey: ['global-search', businessId, debouncedQuery],
    queryFn: () => adminSearchApi.search(businessId, debouncedQuery),
    enabled: open && !!businessId && debouncedQuery.length >= 2,
  });

  const results = useMemo(() => searchQuery.data?.results ?? [], [searchQuery.data]);

  const groupedResults = useMemo(() => {
    const groups = new Map<GlobalSearchResultType, GlobalSearchResultItem[]>();
    for (const result of results) {
      const existing = groups.get(result.type) ?? [];
      existing.push(result);
      groups.set(result.type, existing);
    }
    return SECTION_ORDER.filter((type) => groups.has(type)).map((type) => ({
      type,
      items: groups.get(type)!,
    }));
  }, [results]);

  // Flattened in display order (grouped by type), not API-response order,
  // so keyboard navigation matches what's visually rendered.
  const orderedResults = useMemo(
    () => groupedResults.flatMap((group) => group.items),
    [groupedResults]
  );
  const indexByKey = useMemo(() => {
    const map = new Map<string, number>();
    orderedResults.forEach((result, index) => map.set(`${result.type}-${result.id}`, index));
    return map;
  }, [orderedResults]);

  // Clamp instead of resetting via effect: keeps the selection valid as the
  // result set shrinks/grows without an extra render pass.
  const clampedSelectedIndex =
    orderedResults.length === 0 ? -1 : Math.min(selectedIndex, orderedResults.length - 1);

  function handleSelect(result: GlobalSearchResultItem) {
    navigate(resultHref(result));
    onClose();
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (orderedResults.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % orderedResults.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + orderedResults.length) % orderedResults.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const selected = orderedResults[clampedSelectedIndex];
      if (selected) handleSelect(selected);
    }
  }

  return (
    <AppModal open={open} onClose={onClose} size="lg" hideCloseButton>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
        <AppTextField
          inputRef={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('admin:search.placeholder')}
          startIcon={<Search size={18} />}
          autoFocus
          role="combobox"
          aria-expanded={orderedResults.length > 0}
          // eslint-disable-next-line i18next/no-literal-string -- DOM id reference, not translatable text
          aria-controls="command-palette-listbox"
          aria-activedescendant={
            clampedSelectedIndex >= 0 && orderedResults[clampedSelectedIndex]
              ? `command-palette-option-${orderedResults[clampedSelectedIndex].type}-${orderedResults[clampedSelectedIndex].id}`
              : undefined
          }
        />

        <Box
          id="command-palette-listbox"
          role="listbox"
          sx={{ maxHeight: '420px', overflowY: 'auto' }}
        >
          {debouncedQuery.length < 2 && (
            <Box sx={{ padding: spacing[4], color: colors.textMuted, fontSize: '14px' }}>
              {t('admin:search.hint')}
            </Box>
          )}

          {debouncedQuery.length >= 2 && searchQuery.isFetching && !searchQuery.data && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[2],
                padding: spacing[2],
              }}
            >
              {[1, 2, 3].map((i) => (
                <AppSkeleton key={i} variant="rounded" height={40} />
              ))}
            </Box>
          )}

          {debouncedQuery.length >= 2 && !searchQuery.isFetching && results.length === 0 && (
            <Box sx={{ padding: spacing[4], color: colors.textMuted, fontSize: '14px' }}>
              {t('admin:search.noResults', { query: debouncedQuery })}
            </Box>
          )}

          {groupedResults.map((group) => (
            <Box key={group.type} sx={{ marginBottom: spacing[2] }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[2],
                  padding: `${spacing[2]} ${spacing[3]}`,
                  color: colors.textMuted,
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                }}
              >
                {SECTION_ICONS[group.type]}
                {t(`admin:search.sections.${group.type}`)}
              </Box>
              {group.items.map((result) => {
                const itemIndex = indexByKey.get(`${result.type}-${result.id}`) ?? -1;
                const isSelected = itemIndex === clampedSelectedIndex;
                return (
                  <Box
                    key={`${result.type}-${result.id}`}
                    id={`command-palette-option-${result.type}-${result.id}`}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setSelectedIndex(itemIndex)}
                    onClick={() => handleSelect(result)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      padding: `${spacing[2]} ${spacing[3]}`,
                      borderRadius: radius.md,
                      cursor: 'pointer',
                      backgroundColor: isSelected ? colors.accentLighter : 'transparent',
                    }}
                  >
                    <Box
                      sx={{
                        fontFamily: fonts.body,
                        fontSize: '14px',
                        fontWeight: 500,
                        color: colors.textPrimary,
                      }}
                    >
                      {result.title}
                    </Box>
                    {result.subtitle && (
                      <Box sx={{ fontSize: '12px', color: colors.textMuted }}>
                        {result.subtitle}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </AppModal>
  );
}
